import { createSelector } from 'reselect'
import { ticketSaleSteps } from '../../constants/ticket-sale.constants'
import { serviceChargeHelper } from '../../helpers/service-charge.helper'
import TripPriceHelper from '../../helpers/trip-price.helper'

const selectedBusCompany = ({ ticketSale: { busCompany } }) => busCompany
const saleStepSelector = ({ ticketSale: { saleStep } }) => saleStep
const serviceChargeValueSelector = ({
	creditCardPurchase: { installmentOptions },
}) => installmentOptions
const buyerCitySelector = ({ ticketSale: { buyerCity } }) => buyerCity
const buyerStateSelector = ({ ticketSale: { buyerState } }) => buyerState
const groupGoingSelector = ({ ticketSale: { groupGoing } }) => groupGoing
const tripsGoingSelector = ({ ticketSale: { tripsGoing } }) => tripsGoing
const buyerStreetSelector = ({ ticketSale: { buyerStreet } }) => buyerStreet
const groupReturnSelector = ({ ticketSale: { groupReturn } }) => groupReturn
const raceDateReturnSelector = ({ ticketSale: { raceDateReturn } }) =>
	raceDateReturn
const tripsReturnSelector = ({ ticketSale: { tripsReturn } }) => tripsReturn
const buyerZipCodeSelector = ({ ticketSale: { buyerZipCode } }) => buyerZipCode
const serviceGoingSelector = ({ ticketSale: { serviceGoing } }) => serviceGoing
const raceDateGoingSelector = ({ ticketSale: { raceDateGoing } }) =>
	raceDateGoing
const serviceReturnSelector = ({ ticketSale: { serviceReturn } }) =>
	serviceReturn
const tripDateGoingSelector = ({ ticketSale: { tripDateGoing } }) =>
	tripDateGoing

const originStateSelector = ({ ticketSale: { originTitle } }) =>
	originTitle && originTitle.state ? originTitle.state : ''

const destinationStateSelector = ({ ticketSale: { destinationTitle } }) =>
	destinationTitle && destinationTitle.state ? destinationTitle.state : ''

const serviceChargesSelector = ({ ticketSale: { serviceCharges } }) =>
	serviceCharges
const tripDateReturnSelector = ({ ticketSale: { tripDateReturn } }) =>
	tripDateReturn
const originIdSelector = ({ ticketSale: { originId } }) => originId
const buyerNeighborhoodSelector = ({ ticketSale: { buyerNeighborhood } }) =>
	buyerNeighborhood
const selectedTripGoingSelector = ({ ticketSale: { selectedTripGoing } }) =>
	selectedTripGoing
const selectedTripReturnSelector = ({ ticketSale: { selectedTripReturn } }) =>
	selectedTripReturn
const selectedPriceGoingSelector = ({ ticketSale: { selectedPriceGoing } }) =>
	selectedPriceGoing
const selectedPriceReturnSelector = ({ ticketSale: { selectedPriceReturn } }) =>
	selectedPriceReturn
const destinationIdSelector = ({ ticketSale: { destinationId } }) =>
	destinationId
const selectedServiceChargeSelector = ({
	ticketSale: { selectedServiceCharge },
}) => selectedServiceCharge

const ordemViagemIdaSelector = ({ ticketSale: { ordemViagemIda } }) =>
	ordemViagemIda

const etapaPagamentoSelector = ({ ticketSale: { etapaPagamento } }) =>
	etapaPagamento

const busSeatsGoingSelector = ({ busSeatSelection }) =>
	busSeatSelection.busSeatsGoing

const poltronasIdaSelector = ({ busSeatSelection }) =>
	busSeatSelection.poltronasIda

const busSeatsReturnSelector = ({ busSeatSelection }) =>
	busSeatSelection.busSeatsReturn

const calculatedValueSelector = ({ ticketSale: { calculatedValues } }) =>
	calculatedValues

const familyDiscountParametersSelector = ({
	ticketSale: { familyDiscountParameters },
}) => familyDiscountParameters

const getSeatsQuantityGoing = createSelector(
	[busSeatsGoingSelector],
	(seats) => seats.filter((item) => !item.available).length
)

const getSeatsQuantityReturn = createSelector(
	[busSeatsReturnSelector],
	(seats) => seats.filter((item) => !item.available).length
)

const tripsGoingLengthSelector = createSelector(
	[tripsGoingSelector],
	({ length }) => length
)

const getRouteClassType = createSelector(
	[originStateSelector, destinationStateSelector],
	(origin, destination) => {
		const isIntermunicipal = origin === 'RS' && destination === 'RS'
		const isInterstate = !isIntermunicipal
		return {
			isInterstate,
			isIntermunicipal,
		}
	}
)

const getServiceChargeSelector = createSelector(
	[
		getRouteClassType,
		serviceChargesSelector,
		selectedServiceChargeSelector,
		selectedBusCompany,
	],
	({ isInterstate }, serviceCharges, purchaseOption, busCompany) => {
		const valInterState = isInterstate == true || isInterstate > 0
		const routeClassCode = +valInterState
		const sc = serviceCharges.filter(
			(serviceCharge) =>
				serviceCharge.purchaseOption === purchaseOption &&
				serviceCharge.routeClassCode === routeClassCode &&
				serviceCharge.busCompany.toUpperCase() === busCompany.toUpperCase()
		)
		return sc[0]
	}
)

const tripsReturnLengthSelector = createSelector(
	[tripsReturnSelector],
	({ length }) => length
)

const isBusSelectionGoingStep = createSelector(
	[saleStepSelector],
	(saleStep) => saleStep == ticketSaleSteps.busSeatSelectionGoing
)

const isBusSelectionReturnStep = createSelector(
	[saleStepSelector],
	(saleStep) => saleStep == ticketSaleSteps.busSeatSelectionReturn
)

const isPaymentStep = createSelector(
	[saleStepSelector],
	(saleStep) => saleStep == ticketSaleSteps.payment
)

const isConfirmationStep = createSelector(
	[saleStepSelector],
	(saleStep) => saleStep == ticketSaleSteps.confirmation
)

const getTripsGoing = createSelector([tripsGoingSelector], (tripsGoing) =>
	tripsGoing.map(tripsMapping)
)

const getTripPriceGoing = createSelector(
	[selectedPriceGoingSelector],
	(price) => price
)

const getTripGoing = createSelector(
	[tripsGoingSelector, selectedTripGoingSelector],
	(trips, selectedTrip) => trips[selectedTrip]
)

const getOrdemViagemIda = createSelector(
	[ordemViagemIdaSelector],
	(ordemViagemIda) => ordemViagemIda
)

const getEtapaPagamento = createSelector(
	[etapaPagamentoSelector],
	(etapaPagamento) => etapaPagamento
)

const getTripsReturn = createSelector(
	[tripsReturnSelector, getTripGoing],
	(tripsReturn, tripGoing) => {
		const arrival = tripGoing ? new Date(tripGoing.arrival) : 0

		return tripsReturn
			.filter((trip) => arrival < new Date(trip.departure))
			.map(tripsMapping)
	}
)

// TODO: verificar selectedPriceGoing e selectedPriceReturn
const getTripPriceReturn = createSelector(
	[selectedPriceReturnSelector],
	(price) => price
)

const calculateTripPriceGoing = createSelector(
	[getTripPriceGoing, getSeatsQuantityGoing],
	(price, quantity) => ({
		id: price.id,
		value: price.value * quantity,
		points: price.points * quantity,
		forSubscribers: price.forSubscribers,
		originalValue: price.originalValue * quantity,
	})
)

const calculateTripPriceReturn = createSelector(
	[getTripPriceReturn, getSeatsQuantityReturn],
	(price, quantity) => ({
		id: price.id,
		value: price.value * quantity,
		points: price.points * quantity,
		forSubscribers: price.forSubscribers,
		originalValue: price.originalValue * quantity,
	})
)

const hasNoTripGoingSelected = createSelector(
	[selectedTripGoingSelector, tripsGoingLengthSelector],
	(selectedTrip, length) => selectedTrip === null || length === 0
)

const hasNoTripReturnSelected = createSelector(
	[selectedTripReturnSelector, tripsReturnLengthSelector],
	(selectedTrip, length) => selectedTrip === null || length === 0
)

const getTripGoingSummary = createSelector(
	[tripDateGoingSelector, hasNoTripGoingSelected, getTripGoing],
	(tripDate, hasNoTripSelected, trip) => {
		if (hasNoTripSelected || !trip) return {}
		return {
			tripDate,
			busClass: trip.busClass,
			departure: trip.departure,
			busCompany: trip.enterprise,
		}
	}
)

const getTripReturn = createSelector(
	[tripsReturnSelector, selectedTripReturnSelector, getTripGoing],
	(trips, selectedTrip, tripGoing) => {
		const arrival = tripGoing ? new Date(tripGoing.arrival) : 0
		trips = trips.filter((trip) => arrival < new Date(trip.departure))
		return trips[selectedTrip]
	}
)

const getTripReturnSummary = createSelector(
	[tripDateReturnSelector, hasNoTripReturnSelected, getTripReturn],
	(tripDate, hasNoTripSelected, trip) => {
		if (hasNoTripSelected) return {}
		return {
			tripDate,
			busClass: trip ? trip.busClass : '',
			departure: trip ? trip.departure : '',
			busCompany: trip ? trip.enterprise : '',
		}
	}
)

const getBusSearchParametersGoing = createSelector(
	[
		getTripGoing,
		groupGoingSelector,
		serviceGoingSelector,
		originIdSelector,
		destinationIdSelector,
		raceDateGoingSelector,
	],
	(tripGoing = {}, group, service, originId, destinationId) => ({
		group,
		service,
		originId,
		destinationId,
		tripDate: tripGoing.departure,
		classCode: tripGoing.classCode,
		busCompany: tripGoing.enterprise,
		scheduleKey: tripGoing.scheduleKey,
		raceDate: tripGoing.raceDate,
		provider: tripGoing.provider,
	})
)

const getBusSearchParametersReturn = createSelector(
	[
		getTripReturn,
		groupReturnSelector,
		serviceReturnSelector,
		originIdSelector,
		destinationIdSelector,
		raceDateReturnSelector,
	],
	(tripReturn = {}, group, service) => {
		return {
			group,
			service,
			originId: tripReturn.origemId,
			destinationId: tripReturn.destinoId,
			tripDate: tripReturn.departure,
			classCode: tripReturn.classCode,
			busCompany: tripReturn.enterprise,
			scheduleKey: tripReturn.scheduleKey,
			raceDate: tripReturn.raceDate,
			provider: tripReturn.provider,
		}
	}
)

const getSelectedBusSeatsGoing = createSelector(
	[busSeatsGoingSelector],
	(busSeats) => busSeats.filter(({ available }) => !available)
)

const getPoltronasIda = createSelector([poltronasIdaSelector], (busSeats) =>
	busSeats.filter(({ available }) => !available)
)

const getSelectedBusSeatsReturn = createSelector(
	[busSeatsReturnSelector],
	(busSeats) => busSeats.filter(({ available }) => !available)
)

const getServiceChargeValue = createSelector(
	[
		calculateTripPriceGoing,
		calculateTripPriceReturn,
		getServiceChargeSelector,
		getPoltronasIda,
		getSelectedBusSeatsReturn,
	],
	(priceGoing, priceReturn, serviceCharge, poltronasIda, busSeatsReturn) => {
		var busSeatsGoing = poltronasIda
		var insuranceGoing =
			poltronasIda.length > 0
				? busSeatsGoing[0].withInsurance
					? busSeatsGoing[0].price.insurance * busSeatsGoing.length
					: 0
				: 0
		var insuranceReturn =
			busSeatsReturn.length > 0
				? busSeatsReturn[0].withInsurance
					? busSeatsReturn[0].price.insurance * busSeatsReturn.length
					: 0
				: 0

		if (!serviceCharge) return 0

		var totalValueGoing = 0

		poltronasIda.forEach((busSeat) => {
			let objPrice = TripPriceHelper.calculateWithDiscount(
				getTripPriceGoing,
				busSeat.discount,
				busSeat.price
			)
			totalValueGoing +=
				objPrice.tarifa + objPrice.imposto + objPrice.pedagio
		})

		const originalTripPrice =
			totalValueGoing +
			priceReturn.originalValue +
			insuranceGoing +
			insuranceReturn

		return serviceChargeHelper.calculateServiceCharge(
			serviceCharge,
			originalTripPrice,
			originalTripPrice
		)
	}
)

const hasReturnTrip = createSelector(
	[tripsReturnSelector],
	(tripsReturn) => !!tripsReturn.length
)

function tripsMapping(trip) {
	const diff =
		((new Date(trip.arrival) - new Date(trip.departure)) / (60 * 1000 * 60)) %
		24
	const hours = ~~diff
	const minutes = `0${Math.round((diff % 1) * 60)}`.slice(-2)
	const duration = `${hours}:${minutes}`

	return {
		...trip,
		duration,
		price: { value: trip.price },
	}
}

const getBuyerAddress = createSelector(
	[
		buyerCitySelector,
		buyerStateSelector,
		buyerStreetSelector,
		buyerZipCodeSelector,
		buyerNeighborhoodSelector,
	],
	(city, state, street, zipCode, neighborhood) => ({
		city,
		state,
		street,
		zipCode,
		neighborhood,
	})
)

const serviceChargeValue = createSelector(
	[serviceChargeValueSelector],
	(value) => value
)

const hasInstallments = createSelector(
	[serviceChargeValue],

	(serviceCharge) => {
		return serviceCharge > 0
	}
)

const getCalculatedValues = createSelector(
	[calculatedValueSelector],
	(calculatedValue) => {
		return calculatedValue
	})

const getFamilyDiscountParameters = createSelector(
	[familyDiscountParametersSelector],
	(familyDiscountParameters) => familyDiscountParameters
)

const Selectors = {
	getTripGoing,
	getTripReturn,
	getTripsGoing,
	hasReturnTrip,
	isPaymentStep,
	getTripsReturn,
	hasInstallments,
	getBuyerAddress,
	getRouteClassType,
	getTripPriceGoing,
	getTripPriceReturn,
	isConfirmationStep,
	getTripGoingSummary,
	getTripReturnSummary,
	getServiceChargeValue,
	calculateTripPriceGoing,
	isBusSelectionGoingStep,
	calculateTripPriceReturn,
	getServiceChargeSelector,
	isBusSelectionReturnStep,
	getBusSearchParametersGoing,
	getBusSearchParametersReturn,
	getOrdemViagemIda,
	getEtapaPagamento,
	getCalculatedValues,
	getFamilyDiscountParameters,
}

export default Selectors
